import { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Container, Loader } from '@melio/penny';

import { logout } from '../../api/auth';
import { config } from '../../config';
import { sanitizeLogoutRedirectUrl } from '../../utils/sanitizeRedirectUrl';
import { Logger } from '../../monitoring';
import { clearStoredMfaEnrollmentOffer } from '../../hooks/useMfaEnrollmentOffer';

// Non-JSON means something in front of auth-web answered, not auth-web itself — 403/429 is typically an edge/WAF block (noise), 5xx typically means the origin is unreachable (a real incident), so give them distinct messages rather than lumping them with genuine backend errors.
export const classifyLogoutFailureMessage = (message: string, statusCode: number, contentType: string): string => {
  if (contentType.includes('application/json')) {
    return message;
  }
  if (statusCode === 403 || statusCode === 429) {
    return 'Logout revoke blocked upstream of auth-web (non-JSON 403/429 response)';
  }
  if (statusCode >= 500) {
    return 'Logout revoke failed — non-JSON 5xx response, auth-web may be unreachable';
  }
  return message;
};

export const LogoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const performLogout = async () => {
      // A stashed offer belongs to the session being ended here - leaving it in sessionStorage
      // would let it survive into whatever session/user follows in the same tab.
      clearStoredMfaEnrollmentOffer();
      const result = await logout();

      if (result.type === 'error') {
        if (!result.isNetworkError) {
          const errorMessage = classifyLogoutFailureMessage(result.message, result.statusCode, result.contentType);
          Logger.error('Logout failed', { statusCode: result.statusCode, contentType: result.contentType }, new Error(errorMessage));
        }
        window.location.replace(config.errorRedirectUrl);
        return;
      }

      const sanitizedRedirectUrl = sanitizeLogoutRedirectUrl(searchParams.get('redirectUrl'), config.logoutRedirectAllowedBaseUrls);
      if (sanitizedRedirectUrl) {
        window.location.replace(sanitizedRedirectUrl);
      } else {
        navigate({ pathname: '/sign-in', search: location.search }, { replace: true });
      }
    };

    void performLogout();
  }, [searchParams, navigate]);

  return (
    <Container data-testid="logout-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <Loader />
    </Container>
  );
};
